/**
 * Environments page (/admin/environments) — who sees what.
 *
 * Pure (no `@/` imports) so it runs under `node --test`.
 *
 * The page carries two surfaces:
 *   - the PLATFORM matrix (services × development/stage/main + "Create stage"),
 *     fed by `GET /api/cluster/environments?scope=platform` — cluster-api
 *     `routes/environments.py:list_environments` answers 403
 *     `environment_viewer_required` unless the caller is a super-admin or holds
 *     the GLOBAL `environment` read grant (owner decision 2026-10-04; it was
 *     `platform_scope_operator_only` before the 2026-10-03 I8 read, and the
 *     redacted member read that replaced it has been withdrawn) — and
 *     by `GET /api/cluster/config-repo/workloads?branch=` (the denv-new repo);
 *   - the ORG section ("Your environments"), fed by
 *     `GET /api/cluster/environments?scope=customer&owner_org_id=` under
 *     `@require_permission('environment', 'read')`.
 *
 * The matrix is an operator tool: a customer must never see it, its copy, its
 * Refresh button, or the Platform/Organization toggle — and must never cause a
 * request to its endpoints. The client-side signal is the same one NavBar uses
 * for super-admin-only entries: `currentUser.is_super_admin === true`.
 */

/** True only for a super-admin principal (strict `=== true`). */
export function isSuperAdminUser(user) {
  return !!user && typeof user === 'object' && user.is_super_admin === true
}

/**
 * Decide the page layout for a viewer.
 *
 * @param {{registryEnabled: boolean, user: object|null}} ctx
 * @returns {{
 *   canOperate: boolean,       // may see + load the platform matrix
 *   showToggle: boolean,       // Platform/Organization segmented toggle
 *   initialMode: 'platform'|'org',
 *   heroVariant: 'platform'|'org',
 *   showAccessGate: boolean    // flag-off "Operator access required" panel
 * }}
 */
export function environmentsPageView({ registryEnabled, user } = {}) {
  const canOperate = isSuperAdminUser(user)
  const flagOn = registryEnabled === true
  if (flagOn) {
    return {
      canOperate,
      showToggle: canOperate,
      initialMode: canOperate ? 'platform' : 'org',
      heroVariant: canOperate ? 'platform' : 'org',
      showAccessGate: false
    }
  }
  // Flag OFF: the page is the operator console only (pre-P1 behaviour). A
  // non-operator keeps the "Operator access required" panel — no org section
  // exists to fall back to.
  return {
    canOperate,
    showToggle: false,
    initialMode: 'platform',
    heroVariant: 'platform',
    showAccessGate: !canOperate
  }
}

/**
 * Whether the platform matrix section renders for the current mode.
 * @param {{registryEnabled: boolean, user: object|null, mode: string}} ctx
 */
export function showPlatformMatrix({ registryEnabled, user, mode } = {}) {
  if (!isSuperAdminUser(user)) return false
  return registryEnabled !== true || mode === 'platform'
}

/**
 * Which loads the page fires on mount. The matrix is never in the list for a
 * non-super-admin, so its endpoints are never called for a customer.
 * @returns {Array<'matrix'|'org'>}
 */
export function initialLoads(ctx = {}) {
  const view = environmentsPageView(ctx)
  const loads = []
  if (view.canOperate && view.initialMode === 'platform') loads.push('matrix')
  if (ctx.registryEnabled === true && view.initialMode === 'org') loads.push('org')
  return loads
}

/**
 * Run the mount-time loads through the caller's loaders (`{matrix, org}`).
 * Returns the list of loads it started.
 */
export function runInitialLoads(ctx, loaders = {}) {
  const loads = initialLoads(ctx)
  loads.forEach((k) => {
    if (typeof loaders[k] === 'function') loaders[k]()
  })
  return loads
}

/** HTTP status off an axios-style error (or an unwrap()-thrown Error). */
export function errorStatus(err) {
  const s = err?.response?.status ?? err?.status
  return typeof s === 'number' ? s : null
}

export function isForbidden(err) {
  return errorStatus(err) === 403
}

/** Calm copy for a 403 — never the raw "Request failed with status code 403". */
export const FORBIDDEN_NOTE = {
  matrix:
    'Your account no longer has operator access to the platform environments. Sign in again if your access has just changed.',
  org:
    'You don’t have access to your organisation’s environments yet. Ask an organisation admin to grant it.'
}

/**
 * Classify a load failure into what the page shows.
 * @param {'matrix'|'org'} surface
 * @returns {{kind: 'forbidden'|'error', note?: string, message?: string}}
 */
export function loadFailureView(surface, err, fallback = 'Something went wrong. Try again.') {
  if (isForbidden(err)) return { kind: 'forbidden', note: FORBIDDEN_NOTE[surface] || FORBIDDEN_NOTE.org }
  return { kind: 'error', message: err?.message || fallback }
}

/* ─────────────── Standard #18 I8 — member read, operator full, super-admin write ─────────────── */

/**
 * Access levels on the platform-environment surfaces (E2).
 *
 * **Changed 2026-10-04 (owner decision).** A signed-in member with no
 * `environment` read grant reads NOTHING on the platform scope — cluster-api
 * answers `403 environment_viewer_required`. The redacted member read that
 * Standard #18 I8 described until 2026-10-03 is gone: the projection still
 * carried every face's hostname, branch, site, exposure, lifecycle and drift
 * count, which is the platform's own topology.
 *
 *   anonymous   → nothing (the router's requiresAuth sends them to sign in)
 *   member      → nothing, until granted. `canRead` is PROVISIONAL until the
 *                 API answers; a 403 settles it (`forbidden: true`).
 *   operator    → full rows (holds the global `environment` read grant — the
 *                 API decides; the client learns it from a 200)
 *   super-admin → full rows + plan/apply (writes on `scope: platform`)
 */
export const ACCESS_LEVELS = Object.freeze(['anonymous', 'member', 'operator', 'super-admin'])

/**
 * Decide the viewer's level from the principal and what the API answered.
 * Before any load (`meta` undefined, no refusal) a non-super-admin is a
 * provisional member and is promoted to operator only by a full answer.
 *
 * `forbidden: true` is the SETTLED member case — the API said 403, so the
 * page shows the no-access state rather than a spinner or an error.
 *
 * @param {{user: object|null, meta?: object|null, forbidden?: boolean}} ctx
 * @returns {{level: string, canRead: boolean, full: boolean, canPlan: boolean,
 *   canApply: boolean, provisional: boolean, forbidden: boolean}}
 */
export function platformAccess({ user, meta, forbidden } = {}) {
  if (!user || typeof user !== 'object') {
    return { level: 'anonymous', canRead: false, full: false, canPlan: false, canApply: false, provisional: false, forbidden: false }
  }
  if (isSuperAdminUser(user)) {
    return { level: 'super-admin', canRead: true, full: true, canPlan: true, canApply: true, provisional: false, forbidden: false }
  }
  if (forbidden === true) {
    // The API refused: a member with no `environment` read grant.
    return { level: 'member', canRead: false, full: false, canPlan: false, canApply: false, provisional: false, forbidden: true }
  }
  if (meta === undefined || meta === null) {
    return { level: 'member', canRead: true, full: false, canPlan: false, canApply: false, provisional: true, forbidden: false }
  }
  if (meta && typeof meta === 'object' && meta.redacted === true) {
    // Legacy shape: a cluster-api that predates the 2026-10-04 change can
    // still answer `meta.redacted`. Read it as the member level rather than
    // promoting it to operator.
    return { level: 'member', canRead: true, full: false, canPlan: false, canApply: false, provisional: false, forbidden: false }
  }
  return { level: 'operator', canRead: true, full: true, canPlan: true, canApply: false, provisional: false, forbidden: false }
}

/** Where a face opens from the member page: the console for operators, nowhere otherwise. */
export function consoleLinkFor(access, slug) {
  if (!access || !access.full || !slug) return ''
  return `/admin/environments/platform/${encodeURIComponent(slug)}`
}

/** The no-access copy, used by the member page and by `memberPageNote`. */
export const PLATFORM_NO_ACCESS_NOTE =
  'You don\u2019t have access to the platform environments. Reading them needs the ' +
  '`environment` read grant \u2014 ask a platform operator to grant it.'

/** Copy for the member page's header line, by level. */
export function memberPageNote(access) {
  const level = access && access.level
  if (level === 'super-admin') return 'You can open any face in the console and plan or apply a change.'
  if (level === 'operator') return 'You hold the environment read grant: open any face in the console to see its full row.'
  if (access && access.forbidden === true) return PLATFORM_NO_ACCESS_NOTE
  if (access && access.provisional === true) return 'Checking your access to the platform environments\u2026'
  return 'The platform faces are visible to accounts holding the `environment` read grant. Cluster references, recipients and database names stay with operators.'
}
